import { z } from 'zod';
import { dateOnlySchema } from './common';
import { documentBaseSchema } from './documents';
import { patchOf } from './zod-setup';
/**
 * Proforma invoices.
 *
 * ── WHAT A PROFORMA IS, AND WHY IT IS NOT AN INVOICE ──
 * A demand for payment BEFORE the supply happens. It looks like an invoice and
 * shows the tax the buyer will end up paying - which is the whole point, since
 * they are deciding whether to pay it - but no supply has occurred, so:
 *
 *   - no GST liability arises, and it reaches no return
 *   - nobody owes anything, so it is not a receivable
 *   - it cannot be paid; a payment settles a tax invoice
 *   - it must never take a number from the tax-invoice series
 *
 * When the customer agrees it CONVERTS into a real invoice, and only then does
 * any of the above change.
 *
 * The shape is deliberately `createEstimateSchema` (invoices.ts) with a
 * different name: same lines, same expiry, same everything. What differs is
 * entirely in how the two are read downstream, not in what they carry.
 */
export const createProformaSchema = documentBaseSchema
    .extend({
    customerId: z.string().trim().min(1, 'Pick a customer').max(36),
    /** Absent means "allocate the next number in the PI- series". */
    number: z.string().trim().max(60).optional(),
    /**
     * After this the offer lapses. Not a due date: nothing is owed, so nothing
     * can be overdue. A lapsed proforma is a quote to redo, not a debt to chase.
     */
    expiryDate: dateOnlySchema.optional(),
    /** Issue it immediately instead of saving a draft. */
    markAsSent: z.boolean().optional(),
})
    .strict();
export const updateProformaSchema = patchOf(createProformaSchema).strict();
/**
 * An edit that CANNOT issue the document, as a type rather than as a rule.
 *
 * Identical to `updateProformaSchema` with two fields removed: `markAsSent`,
 * which would put the change in front of a customer, and `number`, which would
 * renumber a document somebody is already holding. It exists because a caller
 * that must not be able to do either should be unable to SAY either - a
 * `.strict()` schema rejects both as unknown keys, and the omission is checked
 * by the compiler rather than by whoever reviews the call site.
 *
 * The MCP write tool parses through this one. See `tools-write.ts`.
 */
export const draftProformaRevisionSchema = patchOf(createProformaSchema.omit({ markAsSent: true, number: true })).strict();
/**
 * Why a revision exists.
 *
 * `sent`       it went to the customer. The ordinary one.
 * `converted`  it was minted at conversion because the working copy had drifted
 *              from the last sent revision - so the invoice traces to a
 *              snapshot rather than to a row that has since moved on.
 */
export const PROFORMA_REVISION_REASONS = ['sent', 'converted'];
/**
 * What actually changed between two revisions, in the terms a person would use
 * on the phone.
 *
 * Deliberately coarse. A field-by-field diff of a priced document is mostly
 * noise - every derived total moves when one quantity does - so this reports
 * the four things somebody asks about: the money, the lines, when it lapses,
 * and the words. Anything finer is answered by opening the two revisions.
 */
export function summariseRevisionChange(before, after) {
    const changes = [];
    if (before.total !== after.total) {
        const direction = Number(after.total) > Number(before.total) ? 'up' : 'down';
        changes.push(`Total went ${direction} from ${before.total} to ${after.total}`);
    }
    const added = after.lines.length - before.lines.length;
    if (added > 0)
        changes.push(`${added} line${added === 1 ? '' : 's'} added`);
    if (added < 0)
        changes.push(`${-added} line${added === -1 ? '' : 's'} removed`);
    if (added === 0 && !sameLines(before.lines, after.lines))
        changes.push('Lines changed');
    if (before.expiryDate !== after.expiryDate) {
        changes.push(after.expiryDate ? `Valid until ${after.expiryDate}` : 'Validity date removed');
    }
    if (before.notes !== after.notes || before.terms !== after.terms)
        changes.push('Notes or terms changed');
    return changes;
}
function sameLines(a, b) {
    return a.every((line, i) => {
        const other = b[i];
        return (!!other &&
            line.name === other.name &&
            line.quantity === other.quantity &&
            line.unitRate === other.unitRate &&
            line.net === other.net);
    });
}
/**
 * Turning a proforma into a real invoice.
 *
 * `date` defaults to today rather than to the proforma's own date, and that is
 * the correct default: the tax point is when the supply happens, not when the
 * offer was made. Carrying the proforma's date across would routinely backdate
 * an invoice into a month that has already been filed.
 */
export const convertToInvoiceSchema = z
    .object({
    date: dateOnlySchema.optional(),
    dueDate: dateOnlySchema.optional(),
    /** Issue the invoice straight away rather than leaving it a draft. */
    markAsSent: z.boolean().optional(),
    /** Carry the proforma's number across as the invoice's reference. */
    keepReference: z.boolean().default(true),
})
    .strict();
export const listProformasQuerySchema = z
    .object({
    q: z.string().trim().max(200).optional(),
    customerId: z.string().trim().max(36).optional(),
    status: z.enum(['all', 'draft', 'sent', 'expired', 'converted', 'void', 'open']).default('all'),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    sort: z.enum(['date', 'number', 'customer', 'total']).default('date'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
})
    .strict();
/**
 * What a proforma shows as.
 *
 * `draft`      not issued
 * `sent`       issued, waiting on the customer
 * `expired`    past its validity with nothing decided
 * `converted`  became a real invoice; that invoice is the live document now
 * `void`       withdrawn
 */
export const PROFORMA_STATUSES = ['draft', 'sent', 'expired', 'converted', 'void'];
/**
 * Derive what to show from what is stored.
 *
 * Same rule as every other status in this codebase: nothing here is written to
 * a column. `converted` in particular is derived from the existence of an
 * invoice pointing back at this proforma - which is why converting one needs no
 * schema change and cannot leave a stale flag behind if that invoice is later
 * deleted.
 *
 * `asOf` is passed in rather than read from the clock so the caller controls the
 * timezone. "Expired" is a question about the customer's calendar day, and a
 * server in UTC deciding it lapses an Indian proforma five and a half hours
 * early.
 */
export function deriveProformaStatus(input) {
    const { lifecycle, expiryDate, converted, asOf } = input;
    // A human's decision always wins over anything derived.
    if (lifecycle === 'void')
        return 'void';
    // Converted outranks expired: once it has become an invoice, what its
    // validity date says stopped mattering.
    if (converted)
        return 'converted';
    if (lifecycle === 'draft')
        return 'draft';
    if (expiryDate && expiryDate < asOf)
        return 'expired';
    return 'sent';
}
/** Whether a proforma in this state can still become an invoice. */
export function canConvert(status) {
    // Expired IS convertible - a lapsed validity date is a conversation with the
    // customer, not a lock. Only a withdrawn one, or one already converted, is
    // genuinely finished.
    return status !== 'void' && status !== 'converted';
}
//# sourceMappingURL=proformas.js.map